"use client";

import { DirectusImage } from "@/components/DirectusImage";
import { FeatureApplicationType } from "./types";

import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { ChevronsRight } from "lucide-react";
import { useScopedI18n } from "@/messages/client";
import RouterLink from "@/components/ui/router-link";
import useLangData from "@/hooks/useLangData";
import { useCallback, useEffect, useState } from "react";
import { cn } from "@/lib/utils";

export default function FeatureApplicationClient(props: {
  data: FeatureApplicationType[];
}) {
  const t = useScopedI18n("产品分类");

  const [current, setCurrent] = useState(0);

  const [mainApi, setMainApi] = useState<CarouselApi | null>(null);

  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const getLangData = useLangData();

  const onSelect = useCallback(() => {
    if (!mainApi) {
      return;
    }

    setCurrent(mainApi.selectedScrollSnap() || 0);
  }, [mainApi]);

  useEffect(() => {
    if (!mainApi) {
      return;
    }
    setScrollSnaps(mainApi.scrollSnapList());

    mainApi.on("reInit", () => {
      setScrollSnaps(mainApi.scrollSnapList());
    });
  }, [mainApi]);

  useEffect(() => {
    if (!mainApi) {
      return;
    }
    onSelect();
    mainApi.on("select", onSelect).on("reInit", onSelect);
  }, [mainApi, onSelect]);

  return (
    <div className="w-full pt-5 md:pb-0 md:pt-8 lg:pt-8">
      <Carousel opts={{ loop: true }} setApi={setMainApi}>
        <CarouselContent className="-ml-4 box-content py-4">
          {props.data?.map((item, index) => (
            <CarouselItem
              className="basis-full pl-4 md:basis-1/2 lg:basis-1/3"
              key={index}>
              <RouterLink
                href={`/application/${item.slug}`}
                className="group relative mx-auto flex h-[300px] w-full max-w-[400px] flex-col rounded-[18px] bg-white p-2.5 transition-all duration-300 hover:shadow-[0px_4px_16px_rgba(0,0,0,0.1)] md:h-[23.75rem] md:rounded-[14px] ">
                <div className="relative h-[170px] w-full overflow-hidden rounded-[18px] md:h-52 md:rounded-[14px]">
                  {item.image && (
                    <DirectusImage
                      className="object-cover object-center"
                      id={item.image}
                      alt={item.name}
                      fill
                    />
                  )}
                </div>

                <div className="flex flex-1 flex-col justify-between md:p-4">
                  <div className="flex items-end space-x-2 pt-2 text-[#404040] md:pt-0">
                    <span className="text-xs md:text-base">
                      {getLangData(
                        "name",
                        item.category?.name,
                        item.category?.lang_data
                      )}
                    </span>
                  </div>

                  <div className="line-clamp-2 text-base text-[#404040] md:text-2xl">
                    {getLangData("name", item.name, item.lang_data)}
                  </div>

                  <div className="flex justify-end">
                    <div className="flex items-center text-xs text-[#919191] transition-colors duration-300 group-hover:text-primary">
                      <span>{t("了解更多")}</span>
                      <ChevronsRight size={14} />
                    </div>
                  </div>
                </div>
              </RouterLink>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      <div className="flex w-full items-center justify-center space-x-[10px] md:hidden">
        {scrollSnaps.map((_, index) => (
          <div
            key={index}
            className={cn(
              "h-2 w-2 cursor-pointer rounded-full bg-[#CCCCCC] transition-all duration-300 ease-in-out",
              {
                "w-5 bg-[#2C2C2C]": current === index,
              }
            )}></div>
        ))}
      </div>
    </div>
  );
}
